/**
 * ESS Climate Intelligence — Data & Methodology Modal
 * Fixed button + modal with full source table
 */

export function createDataMethodologyModal() {
  const wrapper = document.createElement('div');

  // Fixed floating button
  const btn = document.createElement('button');
  btn.className = 'data-info-btn';
  btn.id = 'dataInfoBtn';
  btn.innerHTML = `
    <svg width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
    Data &amp; Methodology
  `;

  // Modal
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.id = 'dataModalOverlay';
  overlay.innerHTML = `
    <div class="modal" id="dataModal">
      <div class="modal__header">
        <h2 class="modal__title">Data Sources &amp; Methodology</h2>
        <button class="modal__close" id="dataModalClose">✕</button>
      </div>

      <div class="modal-section">
        <h3 class="modal-section__title">ENSO Monitoring</h3>
        <table class="modal-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Source</th>
              <th>Frequency</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>RONI (Relative Oceanic Niño Index)</td>
              <td><a href="https://www.cpc.ncep.noaa.gov" target="_blank">NOAA Climate Prediction Center</a></td>
              <td>Monthly (by 5th)</td>
              <td>3-month running mean. Recent values subject to revision.</td>
            </tr>
            <tr>
              <td>Niño 3.4 SST Anomaly</td>
              <td><a href="https://www.cpc.ncep.noaa.gov" target="_blank">NOAA Climate Prediction Center</a></td>
              <td>Monthly (by 5th)</td>
              <td>OISST-based (sstoi.indices). Region: 5°N–5°S, 170°W–120°W.</td>
            </tr>
            <tr>
              <td>ENSO Probability Outlook</td>
              <td><a href="https://www.cpc.ncep.noaa.gov" target="_blank">NOAA CPC ENSO Advisory</a></td>
              <td>Monthly (2nd Thursday)</td>
              <td>Official consensus forecast for 9 upcoming 3-month seasons.</td>
            </tr>
            <tr>
              <td>SST Anomaly Map</td>
              <td><a href="https://www.ncei.noaa.gov" target="_blank">NOAA NCEI OISST v2.1</a></td>
              <td>Daily (via THREDDS WMS)</td>
              <td>0.25° resolution. Live WMS tile service.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="modal-section">
        <h3 class="modal-section__title">Temperature and Regional Climate Charts</h3>
        <table class="modal-table">
          <thead>
            <tr>
              <th>Chart</th>
              <th>Source</th>
              <th>Method</th>
              <th>Update status</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Global monthly surface temperature</td>
              <td>C3S / ECMWF ERA5</td>
              <td>Values rounded from the published C3S monthly chart</td>
              <td>Through September 2026</td>
            </tr>
            <tr>
              <td>WMO multi-model Niño 3.4 outlook</td>
              <td>WMO Global Seasonal Climate Update</td>
              <td>Displayed to the precision available in the published figure</td>
              <td>November 2026 to April 2027</td>
            </tr>
            <tr>
              <td>Selected-region air temperature</td>
              <td>NASA POWER Monthly API</td>
              <td>Mean of the named monitoring points shown below each chart</td>
              <td>Live API, cached for 6 hours</td>
            </tr>
            <tr>
              <td>Selected-region rainfall anomaly</td>
              <td>NASA POWER Monthly API</td>
              <td>Monitoring-point mean compared with the 1991–2020 monthly mean</td>
              <td>Live API, missing months remain empty</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="modal-section">
        <h3 class="modal-section__title">Important Caveats</h3>
        <div class="modal-caveats">
          <div class="modal-caveat">
            <strong>Live data coverage:</strong> RONI, Niño 3.4 and the ENSO outlook refresh from NOAA CPC every six hours. The Pacific SST layer comes from NOAA NCEI. Selected-region temperature and rainfall charts are requested from NASA POWER and cached for six hours. The dashboard does not fill gaps with generated values.
          </div>
          <div class="modal-caveat">
            <strong>RONI Revisions:</strong> NOAA CPC updates RONI by the 5th of each month. The most recent 1–2 months
            of RONI values may be revised due to the high-frequency filter applied to the underlying ERSSTv6 dataset.
          </div>
          <div class="modal-caveat">
            <strong>Not a Forecast:</strong> This dashboard presents observed climate conditions and probabilistic
            ENSO outlooks. It does not constitute a crop yield forecast or agricultural advisory.
          </div>
        </div>
      </div>

      <div class="modal-footer source-note">
        ESS Climate Intelligence · Earth Scan Systems · Dashboard v1.0 · Oct 2026
      </div>
    </div>
  `;

  wrapper.appendChild(btn);
  wrapper.appendChild(overlay);

  // Bind events after insertion
  requestAnimationFrame(() => {
    btn.addEventListener('click', () => overlay.classList.add('open'));
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) overlay.classList.remove('open');
    });
    const closeBtn = overlay.querySelector('#dataModalClose');
    if (closeBtn) closeBtn.addEventListener('click', () => overlay.classList.remove('open'));
  });

  injectModalStyles();
  return wrapper;
}

function injectModalStyles() {
  if (document.getElementById('modal-extra-styles')) return;
  const style = document.createElement('style');
  style.id = 'modal-extra-styles';
  style.textContent = `
    .modal-section {
      margin-bottom: var(--space-6);
    }

    .modal-section__title {
      font-size: var(--text-sm);
      font-weight: var(--weight-semi);
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--clr-accent);
      font-family: var(--font-mono);
      margin-bottom: var(--space-3);
      padding-bottom: var(--space-2);
      border-bottom: 1px solid var(--clr-border);
    }

    .modal-table {
      width: 100%;
      border-collapse: collapse;
      font-size: var(--text-xs);
    }

    .modal-table th {
      text-align: left;
      padding: var(--space-2) var(--space-3);
      color: var(--clr-text-tertiary);
      font-weight: var(--weight-medium);
      font-family: var(--font-mono);
      text-transform: uppercase;
      letter-spacing: 0.06em;
      border-bottom: 1px solid var(--clr-border);
    }

    .modal-table td {
      padding: var(--space-2) var(--space-3);
      color: var(--clr-text-secondary);
      border-bottom: 1px solid rgba(255,255,255,0.04);
      vertical-align: top;
      line-height: 1.5;
    }

    .modal-table tr:last-child td { border-bottom: none; }

    .modal-table a {
      color: var(--clr-accent);
      text-decoration: none;
    }
    .modal-table a:hover { text-decoration: underline; }

    .modal-caveats {
      display: flex;
      flex-direction: column;
      gap: var(--space-3);
    }

    .modal-caveat {
      font-size: var(--text-xs);
      color: var(--clr-text-secondary);
      line-height: 1.6;
      padding: var(--space-3);
      background: var(--clr-surface);
      border-radius: var(--radius-md);
      border-left: 2px solid var(--clr-accent-dim);
    }

    .modal-footer {
      margin-top: var(--space-6);
      padding-top: var(--space-4);
      border-top: 1px solid var(--clr-border);
      text-align: center;
    }
  `;
  document.head.appendChild(style);
}
